import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
// _mock
// types
// assets
import { countries } from 'src/assets/data';
// components
import FormProvider, {
  RHFAutocomplete,
  RHFTextField,
} from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import * as Yup from 'yup';
import { ICustomer } from '../../../@types/customer';
import { useLocales } from '../../../locales';
import { createCustomer, updateCustomer } from '../../../redux/slices/customer';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: (data: any) => void;
  currentCustomer?: ICustomer;
};

export default function CustomerQuickEditForm({
  currentCustomer,
  open,
  onClose,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();

  const isEdit = currentCustomer?.id;

  const NewUserSchema = Yup.object().shape({
    first_name: Yup.string().required(
      `${translate('customerCreatePage.requirements.firstName')}`
    ),
    last_name: Yup.string().required(
      `${translate('customerCreatePage.requirements.lastName')}`
    ),
    email: Yup.string()
      .required(`${translate('customerCreatePage.requirements.email')}`)
      .email(`${translate('customerCreatePage.requirements.validEmail')}`),
    phone: Yup.string().required(
      `${translate('customerCreatePage.requirements.phone')}`
    ),
    address: Yup.string().required(
      `${translate('customerCreatePage.requirements.address')}`
    ),
    city: Yup.string().required(
      `${translate('customerCreatePage.requirements.city')}`
    ),
    country: Yup.string().required(
      `${translate('customerCreatePage.requirements.country')}`
    ),
    zip: Yup.string().required(
      `${translate('customerCreatePage.requirements.zipcode')}`
    ),
    province: Yup.string().required(
      `${translate('customerCreatePage.requirements.province')}`
    ),
  });

  const address =
    currentCustomer?.addresses?.find?.((address) => address.default) ||
    currentCustomer?.addresses?.[0];

  const defaultValues = useMemo(
    () => ({
      first_name: currentCustomer?.first_name || '',
      last_name: currentCustomer?.last_name || '',
      email: currentCustomer?.email || '',
      phone: currentCustomer?.phone || '',
      address: address?.address1 || '',
      city: address?.city || '',
      country: address?.country || '',
      zip: address?.zip || '',
      province: address?.province || '',
    }),
    [currentCustomer, address]
  );

  console.log(defaultValues);

  const methods = useForm<ICustomer>({
    resolver: yupResolver(NewUserSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data: any) => {
    try {
      console.log(data);

      const dataToUpload = {
        id: currentCustomer?.id as string,
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone,
        addresses: [
          {
            address1: data.address,
            city: data.city,
            country: data.country,
            zip: data.zip,
            province: data.province,
            external_id: address?.external_id as string,
          },
        ],
      };

      const rs = isEdit
        ? await updateCustomer(dataToUpload as any)
        : await createCustomer(dataToUpload as any);

      onClose(rs);
      enqueueSnackbar(
        isEdit
          ? `${translate('customerCreatePage.updateSuccess')}`
          : `${translate('customerCreatePage.createSuccess')}`
      );
    } catch (error) {
      console.error(error);
    }
  });

  return (
    <Dialog
      fullWidth
      maxWidth={false}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { maxWidth: 720 },
      }}
    >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>{`${translate(
          isEdit ? 'customersPage.quickEdit' : 'customersPage.quickCreate'
        )}`}</DialogTitle>

        <DialogContent>
          {isEdit && (
            <Alert variant="outlined" severity="info" sx={{ mb: 3 }}>
              {`${translate('customersEditPage.currentlyEditing')} ${
                currentCustomer?.first_name
              }`}
            </Alert>
          )}

          <Box
            rowGap={3}
            columnGap={2}
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
            }}
          >
            <RHFTextField
              name="first_name"
              label={`${translate('customerCreatePage.fields.firstName')}`}
            />
            <RHFTextField
              name="last_name"
              label={`${translate('customerCreatePage.fields.lastName')}`}
            />

            <RHFTextField
              name="email"
              label={`${translate('customerCreatePage.fields.email')}`}
            />
            <RHFTextField
              name="phone"
              label={`${translate('customerCreatePage.fields.phone')}`}
            />
            <RHFTextField
              name="address"
              label={`${translate('customerCreatePage.fields.address')}`}
            />

            <RHFAutocomplete
              name="country"
              label={`${translate('customerCreatePage.fields.country')}`}
              options={countries.map((country) => country.label)}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(option, value) => option === value}
              renderOption={(props, option) => {
                const { code, label, phone } = countries.filter(
                  (country) => country.label === option
                )[0];

                if (!label) {
                  return null;
                }

                return (
                  <li {...props} key={label}>
                    <Iconify
                      key={label}
                      icon={`circle-flags:${code.toLowerCase()}`}
                      width={28}
                      sx={{ mr: 1 }}
                    />
                    {label} ({code}) +{phone}
                  </li>
                );
              }}
            />

            <RHFTextField
              name="city"
              label={`${translate('customerCreatePage.fields.city')}`}
            />
            <RHFTextField
              name="zip"
              label={`${translate('customerCreatePage.fields.zipcode')}`}
            />
            <RHFTextField
              name="province"
              label={`${translate('customerCreatePage.fields.province')}`}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={() => onClose(currentCustomer)}>
            {`${translate('cancel')}`}
          </Button>

          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
          >
            {`${translate(isEdit ? 'update' : 'create')}`}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
